import { db, auth } from '@/firebase/config'
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
  setDoc,
  deleteDoc,
  arrayUnion,
  deleteField
} from 'firebase/firestore'
import {
  generateInvitationToken,
  sendInvitationEmail,
  sendStudentInvitationEmail
} from './invitationService'
import { createLog } from './logService'
import { encryptFields, decryptFields } from './encryptionService'
import {
  INVITATION_HARD_DELETE_AFTER_DAYS,
  isExpiredInviteUser,
  EXPIRED_INVITE_STATUS
} from '@/constants/invitationConstants'

export const addUser = async (user, schoolId) => {
  try {
    const usersRef = collection(db, 'users')
    const encryptedUser = encryptFields(user)
    const docRef = await addDoc(usersRef, {
      ...encryptedUser,
      inviteAt: serverTimestamp(),
      status: 'INVITED',
      schoolId: schoolId
    })

    const token = await generateInvitationToken(
      user.email,
      user.role,
      schoolId,
      docRef.id
    )

    const invitationLink = `${window.location.origin}/educator-access/teacher-account-setup?invitationToken=${token}`

    await sendInvitationEmail(user.email, user.role, invitationLink)

    await createLog({
      userId: auth.currentUser.uid,
      collectionName: 'users',
      data: user,
      schoolId: schoolId,
      action: 'ADD_USER'
    })

    return docRef
  } catch (error) {
    console.error('Error adding user:', error)
    throw error
  }
}

export const getUsers = async (status, schoolId, searchQuery) => {
  const usersRef = collection(db, 'users')
  let q = query(usersRef, where('schoolId', '==', schoolId))

  if (status) {
    q = query(
      usersRef,
      where('schoolId', '==', schoolId),
      where('status', '==', status)
    )
  }

  const querySnapshot = await getDocs(q)
  let users = querySnapshot.docs.map((doc) => {
    const userData = doc.data()
    const decryptedData = decryptFields(userData)
    return {
      ...decryptedData,
      uid: doc.id
    }
  })

  // Filter users based on search query if provided
  if (searchQuery) {
    const searchLower = searchQuery.toLowerCase()
    users = users.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(searchLower) ||
        user.lastName?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower)
    )
  }

  const isInvitationExpired = (user) => isExpiredInviteUser(user)

  // Sort users: expired invitations first, then others
  users.sort((a, b) => {
    const aExpired = isInvitationExpired(a)
    const bExpired = isInvitationExpired(b)

    // Expired invitations first
    if (aExpired && !bExpired) return -1
    if (!aExpired && bExpired) return 1

    // If both expired or both not expired, maintain original order
    return 0
  })

  return users
}

export const getUserById = async (id) => {
  const userRef = doc(db, 'users', id)
  const docSnap = await getDoc(userRef)
  const userData = docSnap.data()
  const decryptedData = decryptFields(userData)
  return {
    ...decryptedData,
    uid: docSnap.id
  }
}

export const checkUserExists = async (email) => {
  try {
    const usersRef = collection(db, 'users')
    const encryptedEmail = encryptFields({ email }).email
    const q = query(usersRef, where('email', '==', encryptedEmail))
    const querySnapshot = await getDocs(q)
    return !querySnapshot.empty
  } catch (error) {
    console.error('Error checking user existence:', error)
    return false
  }
}

/**
 * Check if an email already exists in the users collection (any role, status, school).
 * Uses decryption for comparison because email is stored encrypted (AES with salt),
 * so querying by encrypted value is not reliable.
 * Only compares email; does not filter by role, status, or schoolId.
 * @param {string} email - Normalized email (lowercase, trim)
 * @returns {Promise<{ exists: boolean, existingUserData: object | null }>}
 */
export const checkTeacherEmailInUsersCollection = async (email) => {
  try {
    const normalizedEmail = (email || '').toLowerCase().trim()
    if (!normalizedEmail) return { exists: false, existingUserData: null }

    const usersRef = collection(db, 'users')
    const snapshot = await getDocs(usersRef)

    for (const docSnap of snapshot.docs) {
      const userData = docSnap.data()
      const decryptedData = decryptFields(userData)
      const existingEmail = decryptedData.email?.toLowerCase().trim()
      if (existingEmail === normalizedEmail) {
        return {
          exists: true,
          existingUserData: { uid: docSnap.id }
        }
      }
    }
    return { exists: false, existingUserData: null }
  } catch (error) {
    console.error('Error checking email in users collection:', error)
    return { exists: false, existingUserData: null }
  }
}

export const getUserByEmail = async (email) => {
  try {
    const usersRef = collection(db, 'users')
    const encryptedEmail = encryptFields({ email }).email
    const q = query(usersRef, where('email', '==', encryptedEmail))
    const querySnapshot = await getDocs(q)

    if (querySnapshot.empty) {
      return null
    }

    // Get the first matching user
    const userDoc = querySnapshot.docs[0]
    const userData = userDoc.data()
    const decryptedData = decryptFields(userData)

    return {
      ...decryptedData,
      uid: userDoc.id
    }
  } catch (error) {
    console.error('Error getting user by email:', error)
    return null
  }
}

export const updateUser = async (id, userData) => {
  try {
    const userRef = doc(db, 'users', id)
    const encryptedData = encryptFields(userData)

    await updateDoc(userRef, {
      ...encryptedData,
      updatedAt: serverTimestamp()
    })

    await createLog({
      userId: auth.currentUser.uid,
      collectionName: 'users',
      data: userData,
      schoolId: 'system',
      action: 'UPDATE_USER'
    })

    return { success: true }
  } catch (error) {
    console.error('Error updating user:', error)
    return { success: false, error: error.message }
  }
}

export const createInitialSchoolData = async (
  email,
  numberOfSeats,
  plan,
  paymentId
) => {
  const schoolRef = doc(collection(db, 'schools'))
  const encryptedEmail = encryptFields({ email }).email
  await setDoc(schoolRef, {
    schoolAdminEmail: encryptedEmail,
    numberOfSeats: parseInt(numberOfSeats),
    plan: plan,
    paymentId: paymentId,
    createdAt: serverTimestamp()
  })

  await createLog({
    userId: 'system',
    collectionName: 'schools',
    data: {
      schoolAdminEmail: email,
      numberOfSeats: parseInt(numberOfSeats),
      plan: plan,
      paymentId: paymentId
    },
    schoolId: schoolRef.id,
    action: 'CREATE_SCHOOL'
  })

  return schoolRef.id
}

export const getUserMonthlyStats = async (schoolId) => {
  const usersRef = collection(db, 'users')
  const q = query(
    usersRef,
    where('schoolId', '==', schoolId),
    where('role', 'in', ['STUDENT', 'TEACHER'])
  )
  const querySnapshot = await getDocs(q)
  const users = querySnapshot.docs.map((doc) => {
    const userData = doc.data()
    const decryptedData = decryptFields(userData)
    return {
      ...decryptedData,
      uid: doc.id
    }
  })

  const students = users.filter((user) => user.role === 'STUDENT')
  const teachers = users.filter((user) => user.role === 'TEACHER')

  const totalStudents = students.length
  const totalTeachers = teachers.length

  const now = new Date()
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)

  const lastMonthStudents = students.filter((student) => {
    const createdAt = student.createdAt?.toDate?.() || student.createdAt
    return createdAt >= lastMonth
  })

  const lastMonthTeachers = teachers.filter((teacher) => {
    const createdAt = teacher.createdAt?.toDate?.() || teacher.createdAt
    return createdAt >= lastMonth
  })

  const lastMonthStudentPercentage =
    totalStudents > 0 ? (lastMonthStudents.length / totalStudents) * 100 : 0

  const lastMonthTeacherPercentage =
    totalTeachers > 0 ? (lastMonthTeachers.length / totalTeachers) * 100 : 0

  // Get missions statistics
  let totalMissions = 0
  let lastMonthMissions = 0

  try {
    const missionsRef = collection(db, 'missions')
    const missionsQuery = query(missionsRef, where('schoolId', '==', schoolId))
    const missionsSnapshot = await getDocs(missionsQuery)

    totalMissions = missionsSnapshot.docs.length

    // Count missions created in the last month
    lastMonthMissions = missionsSnapshot.docs.filter((doc) => {
      const createdAt = doc.data().createdAt?.toDate?.() || doc.data().createdAt
      return createdAt && createdAt >= lastMonth
    }).length
  } catch (error) {
    console.error('Error getting missions count:', error)
  }

  const lastMonthMissionsPercentage =
    totalMissions > 0 ? (lastMonthMissions / totalMissions) * 100 : 0

  return {
    students: {
      total: totalStudents,
      lastMonth: lastMonthStudents.length,
      lastMonthPercentage: Math.round(lastMonthStudentPercentage * 100) / 100,
      list: students
    },
    teachers: {
      total: totalTeachers,
      lastMonth: lastMonthTeachers.length,
      lastMonthPercentage: Math.round(lastMonthTeacherPercentage * 100) / 100,
      list: teachers
    },
    missions: {
      total: totalMissions,
      lastMonth: lastMonthMissions,
      lastMonthPercentage: Math.round(lastMonthMissionsPercentage * 100) / 100
    }
  }
}

export const getSchoolInfo = async (schoolId) => {
  if (!schoolId) return null
  const schoolRef = doc(db, 'schools', schoolId)
  const docSnap = await getDoc(schoolRef)
  const decryptedData = decryptFields(docSnap.data())
  return {
    ...decryptedData,
    schoolId: docSnap.id
  }
}

export const saveSchoolDetails = async (schoolDetails, primaryContacts) => {
  if (!auth.currentUser?.uid) {
    throw new Error('You must be signed in to update school details')
  }

  const userRef = doc(db, 'users', auth.currentUser.uid)
  const userSnap = await getDoc(userRef)
  if (!userSnap.exists()) {
    throw new Error('User not found')
  }
  const userSchoolId = userSnap.data()?.schoolId
  if (schoolDetails.schoolId && userSchoolId !== schoolDetails.schoolId) {
    throw new Error('You do not have permission to update this school')
  }

  const encryptedSchoolDetails = encryptFields(schoolDetails)
  const encryptedPrimaryContacts = encryptFields(primaryContacts)

  if (schoolDetails.schoolId) {
    const schoolRef = doc(db, 'schools', schoolDetails.schoolId)
    await setDoc(
      schoolRef,
      {
        ...encryptedSchoolDetails,
        ...encryptedPrimaryContacts,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    )

    await createLog({
      userId: auth.currentUser.uid,
      collectionName: 'schools',
      data: schoolDetails,
      schoolId: schoolDetails.schoolId,
      action: 'UPDATE_SCHOOL'
    })

    return schoolRef
  }

  const schoolRef = collection(db, 'schools')
  const docRef = await addDoc(schoolRef, {
    ...encryptedSchoolDetails,
    ...encryptedPrimaryContacts,
    createdAt: serverTimestamp()
  })

  await createLog({
    userId: auth.currentUser.uid,
    collectionName: 'schools',
    data: schoolDetails,
    schoolId: docRef.id,
    action: 'CREATE_SCHOOL'
  })

  return docRef
}

export const updateSchoolContacts = async (schoolId, contacts) => {
  if (!auth.currentUser?.uid) {
    throw new Error('You must be signed in to update school contacts')
  }

  const userRef = doc(db, 'users', auth.currentUser.uid)
  const userSnap = await getDoc(userRef)
  if (!userSnap.exists()) {
    throw new Error('User not found')
  }
  const userSchoolId = userSnap.data()?.schoolId
  if (userSchoolId !== schoolId) {
    throw new Error('You do not have permission to update this school')
  }

  const schoolRef = doc(db, 'schools', schoolId)
  const encryptedContacts = encryptFields(contacts)
  await updateDoc(schoolRef, {
    ...encryptedContacts,
    updatedAt: serverTimestamp()
  })

  await createLog({
    userId: auth.currentUser.uid,
    collectionName: 'schools',
    data: contacts,
    schoolId: schoolId,
    action: 'UPDATE_SCHOOL_CONTACTS'
  })

  return true
}

export const reinviteUser = async (userId) => {
  try {
    // Get user data
    const userRef = doc(db, 'users', userId)
    const userDoc = await getDoc(userRef)

    if (!userDoc.exists()) {
      throw new Error('User not found')
    }

    const userData = userDoc.data()
    const decryptedData = decryptFields(userData)

    if (
      !decryptedData.email ||
      !decryptedData.role ||
      !decryptedData.schoolId
    ) {
      throw new Error('User data is incomplete')
    }

    // Generate new invitation token
    const token = await generateInvitationToken(
      decryptedData.email,
      decryptedData.role,
      decryptedData.schoolId,
      userId
    )

    // Create invitation link based on role
    let invitationLink
    if (decryptedData.role === 'STUDENT') {
      invitationLink = `${window.location.origin}/educator-access/student-account-setup?invitationToken=${token}`
    } else {
      invitationLink = `${window.location.origin}/educator-access/teacher-account-setup?invitationToken=${token}`
    }

    // Send invitation email based on role
    try {
      if (decryptedData.role === 'STUDENT') {
        await sendStudentInvitationEmail(
          decryptedData.firstName || '',
          decryptedData.lastName || '',
          decryptedData.email,
          invitationLink
        )
      } else {
        await sendInvitationEmail(
          decryptedData.email,
          decryptedData.role,
          invitationLink
        )
      }

      await updateDoc(userRef, {
        inviteAt: serverTimestamp(),
        status: 'INVITED',
        invitationExpiredAt: deleteField()
      })

      await createLog({
        userId: auth.currentUser.uid,
        collectionName: 'users',
        data: decryptedData,
        schoolId: decryptedData.schoolId,
        action: 'REINVITE_USER'
      })

      return { success: true }
    } catch (emailError) {
      // If email sending fails, throw a more user-friendly error
      console.error('Error sending invitation email:', emailError)
      if (emailError.status === 404) {
        throw new Error(
          'Email service configuration error. Please contact support.'
        )
      }
      throw new Error(
        'Failed to send invitation email. Please try again later.'
      )
    }
  } catch (error) {
    console.error('Error reinviting user:', error)
    throw error
  }
}

export const softDeleteUser = async (id) => {
  try {
    // Get user data first
    const userRef = doc(db, 'users', id)
    const userDoc = await getDoc(userRef)

    if (!userDoc.exists()) {
      throw new Error('User not found')
    }

    const userData = userDoc.data()
    const decryptedData = decryptFields(userData)

    // Check if user should be deleted completely
    const isInviteRecord =
      decryptedData.status === 'INVITED' ||
      decryptedData.status === EXPIRED_INVITE_STATUS
    const isDeleted = decryptedData.status === 'DELETED'
    let shouldDeleteCompletely = false

    if (isDeleted) {
      shouldDeleteCompletely = true
    } else if (isInviteRecord && decryptedData.inviteAt) {
      const inviteAt =
        decryptedData.inviteAt?.toDate?.() || decryptedData.inviteAt
      if (inviteAt) {
        const inviteDate =
          inviteAt instanceof Date ? inviteAt : new Date(inviteAt)
        const now = new Date()
        const daysDiff = (now - inviteDate) / (1000 * 60 * 60 * 24)

        if (daysDiff >= INVITATION_HARD_DELETE_AFTER_DAYS) {
          shouldDeleteCompletely = true
        }
      }
    }

    if (
      shouldDeleteCompletely &&
      decryptedData.schoolId &&
      decryptedData.email
    ) {
      // Delete user completely from users collection
      await deleteDoc(userRef)

      // Add email to deletedTeachers array in school document (only for teachers)
      if (decryptedData.role === 'TEACHER') {
        const schoolRef = doc(db, 'schools', decryptedData.schoolId)
        const encryptedEmail = encryptFields({
          email: decryptedData.email
        }).email

        await updateDoc(schoolRef, {
          deletedTeachers: arrayUnion({
            email: encryptedEmail,
            deletedDate: new Date()
          })
        })
      }

      await createLog({
        userId: auth.currentUser.uid,
        collectionName: 'users',
        data: decryptedData,
        schoolId: decryptedData.schoolId,
        action: 'DELETE_USER_PERMANENTLY'
      })
    } else {
      // Soft delete - just update status
      await updateDoc(userRef, {
        status: 'DELETED',
        removedAt: serverTimestamp()
      })

      await createLog({
        userId: auth.currentUser.uid,
        collectionName: 'users',
        data: decryptedData,
        schoolId: decryptedData.schoolId || 'system',
        action: 'SOFT_DELETE_USER'
      })
    }
  } catch (error) {
    console.error('Error deleting user:', error)
    throw error
  }
}
